// Country-aware landing content. Auto-detected (IP) or manually selected;
// swaps the tax terminology that confuses users across regions.
// Client-side progressive enhancement only — the prerendered HTML keeps the US default.

export const COUNTRY_OPTIONS = [
  { key: 'US', flag: '🇺🇸', name: 'United States' },
  { key: 'UK', flag: '🇬🇧', name: 'United Kingdom' },
  { key: 'CA', flag: '🇨🇦', name: 'Canada' },
  { key: 'DE', flag: '🇩🇪', name: 'Germany' },
  { key: 'ES', flag: '🇪🇸', name: 'Spain' },
  { key: 'FR', flag: '🇫🇷', name: 'France' },
  { key: 'UAE', flag: '🇦🇪', name: 'United Arab Emirates' },
  { key: 'KSA', flag: '🇸🇦', name: 'Saudi Arabia' },
  { key: 'AU', flag: '🇦🇺', name: 'Australia' },
  { key: 'NZ', flag: '🇳🇿', name: 'New Zealand' },
  { key: 'GLOBAL', flag: '🌍', name: 'Other / Global' },
];

// Visitor ISO country code -> our region key (anything else -> GLOBAL).
export const CODE_TO_REGION = { US: 'US', GB: 'UK', CA: 'CA', DE: 'DE', ES: 'ES', FR: 'FR', AE: 'UAE', SA: 'KSA', AU: 'AU', NZ: 'NZ' };

export const COUNTRY_CONTENT = {
  US: {
    key: 'US', flag: '🇺🇸', name: 'United States', short: 'US', sym: '$', reportLabel: 'Schedule C',
    fx: null, mileageRate: '$0.76/mi', accountant: 'CPA',
    eyebrow: '1099 self-employment tax',
    calcTo: '/tools/1099-tax-calculator', calcLabel: 'Free 1099 tax calculator',
    heroSub: { pre: 'Text your expenses, track mileage, and get tax-ready —', strong: 'no QuickBooks, no spreadsheets.' },
    pills: ['📸 Snap & scan receipts', '🧾 CPA-ready reports', '📋 1099 & tax-ready', '🌍 Multi-currency'],
    features: {
      autocat: 'Every transaction sorted into the right IRS Schedule C category automatically.',
      mileage: "Text 'drove 22 miles to client' — logged at the 2026 IRS business rate: $0.76/mi from Jul 1 (was $0.725/mi Jan 1–Jun 30).",
      quarterly: 'Estimate what you owe the IRS each quarter based on your tracked income and expenses, with WhatsApp deadline reminders.',
      reports: 'Schedule C Profit & Loss with your mileage log — export to PDF, CSV or Excel.',
      income: 'Track income by client so you can reconcile every 1099 you receive.',
    },
    demoMileage: '“drove 22 miles” logs at $0.76/mi. “got paid 500 from Upwork” tracks 1099 income by client.',
    demoCategory: 'Sorted into the right IRS Schedule C category, with the tax you just saved shown in real time.',
    regionName: 'United States',
  },
  UK: {
    key: 'UK', flag: '🇬🇧', name: 'United Kingdom', short: 'UK', sym: '£', reportLabel: 'Self Assessment (SA103)',
    fx: { sym: '£', rate: 0.79 }, mileageRate: '45p/mile', accountant: 'accountant',
    eyebrow: 'Self Assessment tax',
    calcTo: '/tools/uk-self-assessment-tax-calculator', calcLabel: 'Free Self Assessment calculator',
    heroSub: { pre: 'Text your expenses, track mileage, and get Self Assessment-ready —', strong: 'no spreadsheets, no stress.' },
    pills: ['📸 Snap & scan receipts', '🧾 Accountant-ready reports', '📋 SA103 & tax-ready', '🌍 Multi-currency'],
    features: {
      autocat: 'Every transaction sorted for your HMRC Self Assessment (SA103) automatically.',
      mileage: "Text 'drove 22 miles to client' — logged at HMRC's 45p/mile rate.",
      quarterly: 'Know your income tax + Class 4 NI before the 31 Jan deadline, with WhatsApp reminders.',
      reports: 'Self Assessment (SA103) profit & loss with your mileage log — export PDF, CSV or Excel.',
      income: 'Track income by client so your Self Assessment reconciles cleanly.',
    },
    demoMileage: '“drove 22 miles” logs at 45p/mi. “got paid 500 from a client” tracks your self-employed income.',
    demoCategory: 'Sorted for your HMRC Self Assessment (SA103), with the tax you just saved shown in real time.',
    regionName: 'United Kingdom',
  },
  CA: {
    key: 'CA', flag: '🇨🇦', name: 'Canada', short: 'Canada', sym: '$', reportLabel: 'T2125',
    fx: { sym: 'CA$', rate: 1.37 }, mileageRate: '$0.70/km', accountant: 'CPA',
    eyebrow: 'self-employed tax (T2125)',
    calcTo: '/tools/canada-self-employed-tax-calculator', calcLabel: 'Free T2125 tax calculator',
    heroSub: { pre: 'Text your expenses, track mileage, and get T2125-ready —', strong: 'no spreadsheets, no stress.' },
    pills: ['📸 Snap & scan receipts', '🧾 Accountant-ready reports', '📋 T2125 & tax-ready', '🌍 Multi-currency'],
    features: {
      autocat: 'Every transaction sorted for your CRA T2125 statement automatically.',
      mileage: "Text 'drove 22 km to client' — logged at CRA's $0.70/km rate.",
      quarterly: 'Know your federal tax + CPP and CRA instalments ahead of time, with WhatsApp reminders.',
      reports: 'T2125 statement of business activities with your mileage log — export PDF, CSV or Excel.',
      income: 'Track income by client so your T2125 reconciles cleanly.',
    },
    demoMileage: '“drove 22 km” logs at $0.70/km. “got paid 500 from a client” tracks your self-employed income.',
    demoCategory: 'Sorted for your CRA T2125 statement, with the tax you just saved shown in real time.',
    regionName: 'Canada',
  },
  DE: {
    key: 'DE', flag: '🇩🇪', name: 'Germany', short: 'Germany', sym: '€', reportLabel: 'EÜR',
    fx: { sym: '€', rate: 0.92 }, mileageRate: '€0.30/km', accountant: 'accountant',
    eyebrow: 'freelancer tax (Steuer)',
    calcTo: '/tools/germany-self-employed-tax-calculator', calcLabel: 'Free German tax calculator',
    heroSub: { pre: 'Text your expenses, track mileage, and get Finanzamt-ready —', strong: 'no spreadsheets, no stress.' },
    pills: ['📸 Snap & scan receipts', '🧾 Accountant-ready reports', '📋 EÜR & USt-ready', '🌍 Multi-currency'],
    features: {
      autocat: 'Every transaction sorted for your EÜR and VAT (Umsatzsteuer) automatically.',
      mileage: "Text 'drove 22 km to client' — logged at the €0.30/km rate.",
      quarterly: 'Know your Einkommensteuer + 19% VAT before your Finanzamt deadlines, with WhatsApp reminders.',
      reports: 'EÜR profit & loss with your mileage log — export PDF, CSV or Excel.',
      income: 'Track income by client so your tax return reconciles cleanly.',
    },
    demoMileage: '“drove 22 km” logs at €0.30/km. “got paid 500 from a client” tracks your freelancer income.',
    demoCategory: 'Sorted for your EÜR and VAT (Umsatzsteuer), with the tax you just saved shown in real time.',
    regionName: 'Germany',
  },
  ES: {
    key: 'ES', flag: '🇪🇸', name: 'Spain', short: 'Spain', sym: '€', reportLabel: 'Modelo 130 / Renta',
    fx: { sym: '€', rate: 0.92 }, mileageRate: '€0.26/km', accountant: 'accountant',
    eyebrow: 'autónomo tax (IRPF)',
    calcTo: '/tools/spain-autonomo-tax-calculator', calcLabel: 'Free autónomo tax calculator',
    heroSub: { pre: 'Text your expenses, track mileage, and get autónomo-ready —', strong: 'no spreadsheets, no stress.' },
    pills: ['📸 Snap & scan receipts', '🧾 Accountant-ready reports', '📋 IRPF & IVA-ready', '🌍 Multi-currency'],
    features: {
      autocat: 'Every transaction sorted for your IRPF and 21% IVA automatically.',
      mileage: "Text 'drove 22 km to client' — logged with your deductible expenses.",
      quarterly: 'Know your IRPF and RETA before your Modelo 130/303 deadlines, with WhatsApp reminders.',
      reports: 'Autónomo profit & loss with your expenses — export PDF, CSV or Excel.',
      income: 'Track income by client so your Renta reconciles cleanly.',
    },
    demoMileage: '“drove 22 km” logs your deductible travel. “got paid 500 from a client” tracks your autónomo income.',
    demoCategory: 'Sorted for your IRPF and 21% IVA, with the tax you just saved shown in real time.',
    regionName: 'Spain',
  },
  FR: {
    key: 'FR', flag: '🇫🇷', name: 'France', short: 'France', sym: '€', reportLabel: 'Micro-entrepreneur',
    fx: { sym: '€', rate: 0.92 }, mileageRate: 'your local rate', accountant: 'accountant',
    eyebrow: 'micro-entrepreneur tax',
    calcTo: '/tools/france-auto-entrepreneur-tax-calculator', calcLabel: 'Free micro-entrepreneur calculator',
    heroSub: { pre: 'Text your income, track expenses, and stay URSSAF-ready —', strong: 'no spreadsheets, no stress.' },
    pills: ['📸 Snap & scan receipts', '🧾 Accountant-ready reports', '📋 URSSAF & TVA-ready', '🌍 Multi-currency'],
    features: {
      autocat: 'Every sale and cost sorted for your micro-entreprise declarations automatically.',
      mileage: "Text 'drove 22 km to client' — kept with your business records.",
      quarterly: 'Know your URSSAF cotisations and income tax before each déclaration, with WhatsApp reminders.',
      reports: 'Micro-entrepreneur turnover summary — export PDF, CSV or Excel.',
      income: 'Track turnover by client so your URSSAF declaration reconciles cleanly.',
    },
    demoMileage: '“drove 22 km” stays with your records. “got paid 500 from a client” tracks your turnover.',
    demoCategory: 'Sorted for your URSSAF cotisations and TVA thresholds, with your set-aside shown in real time.',
    regionName: 'France',
  },
  UAE: {
    key: 'UAE', flag: '🇦🇪', name: 'United Arab Emirates', short: 'the UAE', sym: 'AED ', reportLabel: 'VAT return',
    fx: { sym: 'AED ', rate: 3.67 }, mileageRate: 'your local rate', accountant: 'accountant',
    eyebrow: 'UAE VAT (5%)',
    calcTo: '/tools/uae-vat-calculator', calcLabel: 'Free UAE VAT calculator',
    heroSub: { pre: 'Snap your invoices, capture the VAT, and stay FTA-ready —', strong: 'no spreadsheets, no stress.' },
    pills: ['📸 Snap & scan invoices', '🧾 VAT-ready records', '📋 5% VAT & TRN', '🌍 Multi-currency'],
    features: {
      autocat: 'Every invoice captured with its VAT amount and TRN automatically.',
      mileage: "Text 'drove 22 km to client' — kept with your business records.",
      quarterly: 'Know your net VAT before each FTA return, with WhatsApp reminders.',
      reports: 'VAT summary — output VAT, input VAT and net payable — export PDF, CSV or Excel.',
      income: 'Track income by client and keep every tax invoice audit-ready.',
    },
    demoMileage: '“spent 210 on supplies” captures the 5% input VAT. “got paid 2000” records the output VAT.',
    demoCategory: 'Captured with the exact 5% VAT, so your FTA return is ready.',
    regionName: 'the UAE',
  },
  KSA: {
    key: 'KSA', flag: '🇸🇦', name: 'Saudi Arabia', short: 'Saudi Arabia', sym: 'SAR ', reportLabel: 'VAT return',
    fx: { sym: 'SAR ', rate: 3.75 }, mileageRate: 'your local rate', accountant: 'accountant',
    eyebrow: 'Saudi VAT (15%)',
    calcTo: '/tools/saudi-arabia-vat-calculator', calcLabel: 'Free Saudi VAT calculator',
    heroSub: { pre: 'Snap your invoices, capture the VAT, and stay ZATCA-ready —', strong: 'no spreadsheets, no stress.' },
    pills: ['📸 Snap & scan invoices', '🧾 VAT-ready records', '📋 15% VAT & Fatoora', '🌍 Multi-currency'],
    features: {
      autocat: 'Every invoice captured with its 15% VAT amount automatically.',
      mileage: "Text 'drove 22 km to client' — kept with your business records.",
      quarterly: 'Know your net VAT before each ZATCA return, with WhatsApp reminders.',
      reports: 'VAT summary — output VAT, input VAT and net payable — export PDF, CSV or Excel.',
      income: 'Track income by client and keep every tax invoice audit-ready.',
    },
    demoMileage: '“spent 230 on supplies” captures the 15% input VAT. “got paid 2000” records the output VAT.',
    demoCategory: 'Captured with the exact 15% VAT, so your ZATCA return is ready.',
    regionName: 'Saudi Arabia',
  },
  AU: {
    key: 'AU', flag: '🇦🇺', name: 'Australia', short: 'Australia', sym: 'A$', reportLabel: 'Individual tax return',
    fx: { sym: 'A$', rate: 1.52 }, mileageRate: 'A$0.88/km', accountant: 'accountant',
    eyebrow: 'sole trader tax (ATO)',
    calcTo: '/tools/australia-sole-trader-tax-calculator', calcLabel: 'Free sole trader tax calculator',
    heroSub: { pre: 'Text your expenses, track mileage, and stay ATO-ready —', strong: 'no spreadsheets, no stress.' },
    pills: ['📸 Snap & scan receipts', '🧾 Accountant-ready reports', '📋 Income tax & GST-ready', '🌍 Multi-currency'],
    features: {
      autocat: 'Every transaction sorted for your individual tax return automatically.',
      mileage: "Text 'drove 22 km to client' — logged at the ATO 88c/km rate.",
      quarterly: 'Know your income tax and PAYG instalments before each BAS, with WhatsApp reminders.',
      reports: 'Sole trader profit & loss with your expenses — export PDF, CSV or Excel.',
      income: 'Track income by client so your tax return reconciles cleanly.',
    },
    demoMileage: '“drove 22 km” logs at A$0.88/km. “got paid 500 from a client” tracks your business income.',
    demoCategory: 'Sorted for your ATO tax return, with the tax you just saved shown in real time.',
    regionName: 'Australia',
  },
  NZ: {
    key: 'NZ', flag: '🇳🇿', name: 'New Zealand', short: 'New Zealand', sym: 'NZ$', reportLabel: 'IR3 income tax return',
    fx: { sym: 'NZ$', rate: 1.66 }, mileageRate: 'NZ$1.17/km', accountant: 'accountant',
    eyebrow: 'sole trader tax (IRD)',
    calcTo: '/tools/new-zealand-sole-trader-tax-calculator', calcLabel: 'Free sole trader tax calculator',
    heroSub: { pre: 'Text your expenses, track mileage, and stay IRD-ready —', strong: 'no spreadsheets, no stress.' },
    pills: ['📸 Snap & scan receipts', '🧾 Accountant-ready reports', '📋 Income tax & GST-ready', '🌍 Multi-currency'],
    features: {
      autocat: 'Every transaction sorted for your IR3 tax return automatically.',
      mileage: "Text 'drove 22 km to client' — logged at the IRD kilometre rate.",
      quarterly: 'Know your income tax and provisional tax before each instalment, with WhatsApp reminders.',
      reports: 'Sole trader profit & loss with your expenses — export PDF, CSV or Excel.',
      income: 'Track income by client so your tax return reconciles cleanly.',
    },
    demoMileage: '“drove 22 km” logs at the IRD rate. “got paid 500 from a client” tracks your business income.',
    demoCategory: 'Sorted for your IR3 tax return, with the tax you just saved shown in real time.',
    regionName: 'New Zealand',
  },
  GLOBAL: {
    key: 'GLOBAL', flag: '🌍', name: 'Global', short: 'your country', sym: '$', reportLabel: 'Profit & Loss',
    fx: null, mileageRate: 'your local rate', accountant: 'accountant',    eyebrow: 'freelancer tax',
    calcTo: '/tools/1099-tax-calculator', calcLabel: 'Free tax calculator',
    heroSub: { pre: 'Text your expenses, track mileage, and get tax-ready —', strong: 'no spreadsheets, no stress.' },
    pills: ['📸 Snap & scan receipts', '🧾 Accountant-ready reports', '📋 Tax-ready exports', '🌍 Multi-currency'],
    features: {
      autocat: "Every transaction sorted into the right tax category for your country automatically.",
      mileage: "Text 'drove 22 miles to client' — logged at your country's mileage rate.",
      quarterly: 'Know what you owe before your tax deadlines, with WhatsApp reminders.',
      reports: 'Accountant-ready profit & loss with your mileage log — export PDF, CSV or Excel.',
      income: 'Track income by client so your tax return reconciles cleanly.',
    },
    demoMileage: '“drove 22 miles” logs at your local rate. “got paid 500 from a client” tracks your income.',
    demoCategory: 'Sorted into the right tax category for your country, with the tax you just saved shown in real time.',
    regionName: 'your region',
  },
};

export const getCountryContent = (key) => COUNTRY_CONTENT[key] || COUNTRY_CONTENT.GLOBAL;
